import * as moment from 'moment';
import { Sort } from '@angular/material/sort';
import { ThemePalette } from '@angular/material/core';
import { Validators } from '@angular/forms';
import {
  compareByNestedKey,
  compareNumbers,
  compareSeparatorLongStringNumbers,
  compareStringDates,
  compareStrings,
} from '@shared/utils';
import {
  DoubleDateTableFilter,
  getDateRangeChipContent,
  InputTableFilter,
  RadioTableFilter,
  TableFilterConfig,
  TableFilterType,
} from '@shared/components/table-filter-menu';
import { OrderStatus } from '@components/dashboard/fulfillment/fulfillment.enums';
import { creationDateRangeValidator } from '@components/dashboard/fulfillment/components/orders/orders.validators';

export const ORDERS_ITEMS_PER_PAGE = 5;

export const AVAILABLE_DATE_RANGE_MONTH = 2;

export const NPU_DIRECTION_SHIPMENT = 'Відвантаження НПУ';

export const ORDERS_DISPLAYED_COLUMNS = [
  'numberEN',
  'orderStatus',
  'dateCreation',
  'numberOrder',
  'directionShipment',
  'ErrorRegistrationEN',
  'ErrorRegistration',
  'comment',
  'options',
];

export const ORDERS_DISABLE_COLUMNS = [
  { name: 'numberEN', disable: true },
  { name: 'orderStatus', disable: false },
  { name: 'dateCreation', disable: false },
  { name: 'numberOrder', disable: false },
  { name: 'directionShipment', disable: false },
  { name: 'ErrorRegistrationEN', disable: false },
  { name: 'ErrorRegistration', disable: false },
  { name: 'comment', disable: false },
  { name: 'options', disable: true },
];

export const ORDERS_COLUMNS_CONFIG = [
  {
    name: 'Дата створення',
    show: true,
    class: 'dateCreation',
  },
  {
    name: 'Номер замовлення',
    show: true,
    class: 'numberOrder',
  },
  {
    name: 'Спосіб доставки',
    show: true,
    class: 'directionShipment',
  },
  {
    name: 'Помилка реєстрації НП',
    show: true,
    class: 'ErrorRegistrationEN',
  },
  {
    name: 'Помилка реєстрації',
    show: true,
    class: 'ErrorRegistration',
  },
  {
    name: 'Коментарі',
    show: true,
    class: 'comment',
  },
];

export const ORDERS_DEFAULT_SORT_EVENT: Sort = {
  active: 'dateCreation',
  direction: 'desc',
};

export const ORDERS_SORT_CONFIG = {
  numberEN: compareByNestedKey(compareSeparatorLongStringNumbers, 'numberEN'),
  orderStatus: compareByNestedKey(compareStrings, 'status'),
  dateCreation: compareByNestedKey(compareStringDates, 'dateCreation'),
  numberOrder: compareByNestedKey(compareNumbers, 'numberOrder'),
  directionShipment: compareByNestedKey(compareNumbers, 'directionShipment'),
  ErrorRegistrationEN: compareByNestedKey(compareNumbers, 'ErrorRegistrationEN'),
  ErrorRegistration: compareByNestedKey(compareNumbers, 'ErrorRegistration'),
  comment: compareByNestedKey(compareNumbers, 'comment'),
};

export const ORDERS_NUMBER_ORDER_FILTER: InputTableFilter = {
  title: 'Номер замовлення',
  controlName: 'numberOrder',
  type: TableFilterType.Input,
  testId: 'inputFilter',
  initialValue: '',
  apiFieldName: 'orderNumberList',
  validators: [Validators.minLength(1), Validators.maxLength(25)],
  errors: {
    maxlength: 'Максимальна довжина 25 символів',
    minlength: 'Введіть номер замовлення',
  },
  placeholder: 'Введіть номер замовлення',
  hotFilter: {
    type: TableFilterType.Input,
    quickButtonText: 'Номер замовлення',
    overlayTitle: 'Номер замовлення',
    overlayButtonText: 'Знайти у замовленнях',
    order: 1,
  },
  applyTrim: true,
  chipData: {
    content: (value) => `Номер замовлення: ${value}`,
  },
};

export const ORDERS_STATUS_FILTER: RadioTableFilter = {
  title: 'Статус замовлення',
  type: TableFilterType.Radio,
  testId: 'orders-status-filter',
  errors: {},
  placeholder: '',
  controlName: 'orderStatus',
  initialValue: 'Всі',
  apiFieldName: 'orderStatusList',
  validators: [],
  options: [
    { value: 'Всі', label: 'Всі', testId: 'Всі' },
    { value: OrderStatus.New, label: OrderStatus.New, testId: OrderStatus.New },
    { value: OrderStatus.Selected, label: OrderStatus.Selected, testId: OrderStatus.Selected },
    { value: OrderStatus.Packed, label: OrderStatus.Packed, testId: OrderStatus.Packed },
    { value: OrderStatus.Shipped, label: OrderStatus.Shipped, testId: OrderStatus.Shipped },
    { value: OrderStatus.Canceled, label: OrderStatus.Canceled, testId: OrderStatus.Canceled },
    { value: OrderStatus.Blocked, label: OrderStatus.Blocked, testId: OrderStatus.Blocked },
    { value: OrderStatus.ToPlan, label: OrderStatus.ToPlan, testId: OrderStatus.ToPlan },
    { value: OrderStatus.PartiallyPlanned, label: OrderStatus.PartiallyPlanned, testId: OrderStatus.PartiallyPlanned },
    { value: OrderStatus.Planned, label: OrderStatus.Planned, testId: OrderStatus.Planned },
    { value: OrderStatus.ReadyToSelect, label: OrderStatus.ReadyToSelect, testId: OrderStatus.ReadyToSelect },
    { value: OrderStatus.ToSelect, label: OrderStatus.ToSelect, testId: OrderStatus.ToSelect },
    { value: OrderStatus.InSelection, label: OrderStatus.InSelection, testId: OrderStatus.InSelection },
    { value: OrderStatus.Controlled, label: OrderStatus.Controlled, testId: OrderStatus.Controlled },
    { value: OrderStatus.ReadyToShip, label: OrderStatus.ReadyToShip, testId: OrderStatus.ReadyToShip },
  ],
  hotFilter: {
    type: TableFilterType.Radio,
    quickButtonText: 'Статус замовлення',
    overlayTitle: 'Статус замовлення',
    overlayButtonText: 'Застосувати',
    order: 3,
  },
  chipData: {
    content: (value) => value,
  },
};

export const ORDERS_CREATION_DATE_FILTER: DoubleDateTableFilter = {
  title: 'Дата створення',
  type: TableFilterType.DoubleDate,
  testId: 'orders-creation-date-filter',
  errors: {
    range: 'Вказано некоректний діапазон',
  },
  placeholder: '',
  initialValue: '',
  controlName: 'creationDate',
  apiFieldName: 'startPeriod',
  validators: [creationDateRangeValidator],
  hotFilter: {
    type: TableFilterType.DoubleDate,
    quickButtonText: 'Дата створення',
    overlayTitle: 'Дата створення',
    overlayButtonText: 'Застосувати',
    order: 2,
  },
  fromFilter: {
    title: 'Створили з',
    type: TableFilterType.Date,
    testId: 'orders-creation-date-filter-from',
    errors: {},
    placeholder: 'Не обрано',
    controlName: 'creationDateFrom',
    initialValue: null,
    apiFieldName: 'startPeriod',
    validators: [],
    minDate: moment().subtract(2, 'month').toDate(),
    maxDate: moment().toDate(),
  },
  toFilter: {
    title: 'по',
    type: TableFilterType.Date,
    testId: 'orders-creation-date-filter-to',
    errors: {
      range: 'Вказано некоректний діапазон',
    },
    placeholder: 'не обрано',
    controlName: 'creationDateTo',
    initialValue: null,
    apiFieldName: 'endPeriod',
    validators: [],
    minDate: moment().subtract(2, 'month').toDate(),
    maxDate: moment().toDate(),
  },
  chipData: {
    content: (value) => getDateRangeChipContent(value, ORDERS_CREATION_DATE_FILTER, 'Створили'),
  },
};

export const ORDERS_FILTER_CONFIG: TableFilterConfig = {
  title: 'Фільтри',
  storageKey: 'orders-filters',
  buttons: {
    clearFilters: {
      text: 'Очистити все',
      testId: 'clearFiltersBtn',
      autofocus: false,
      class: 'btn',
    },
    applyFilters: {
      text: 'Застосувати фільтри',
      testId: 'applyFiltersBtn',
      color: 'primary' as ThemePalette,
    },
  },
  filterGroups: [
    {
      title: 'Дані про замовлення',
      testId: 'group1',
      controlName: 'group1',
      expandedByDefault: true,
      filters: [ORDERS_NUMBER_ORDER_FILTER, ORDERS_CREATION_DATE_FILTER, ORDERS_STATUS_FILTER],
    },
  ],
};
